import LaunchIcon from "@mui/icons-material/Launch";
import { Box, Divider, Grid, IconButton, Stack } from "@mui/material";
import React, { useCallback, useState } from "react";

import { useLabels } from "api/services/useLabels";
import { ImageCanvas } from "components/common/ImageCanvas";
import { getIQImage } from "features/canvas/utils";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { AnswerBar } from "new-components/answerBar/AnswerBar";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { setIsFocusModalOpen, setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ImageQuery, Posicheck } from "types/imageQueries";
import { UserType } from "types/users";
import { getLabelColor } from "utils/ReviewPageUtils";
import { fromBackendROIs } from "utils/roiConversion";

interface ImageItemProps {
  example: ImageQuery;
  showOpenModal?: boolean;
  onOpenModal?: () => void;
  setSelectedExampleImageQuery: (example: ImageQuery) => void;
}

function ImageItem({
  example,
  showOpenModal = false,
  onOpenModal,
  setSelectedExampleImageQuery,
}: ImageItemProps) {
  const { labels } = useLabels(example.iq_id);
  const rois = fromBackendROIs(labels?.results?.[0]);
  const [imageStatus, setImageStatus] = React.useState<"loading" | "loaded" | "failed">("loading");

  const image = React.useMemo(() => {
    const img = getIQImage(example.iq_id);
    img.onload = () => {
      setImageStatus("loaded");
    };
    return img;
  }, [example.iq_id]);

  const handleOpenModal = () => {
    if (onOpenModal) {
      onOpenModal();
    }
    setSelectedExampleImageQuery(example);
  };

  return (
    <Box
      data-testid={`modal-example-image-item-${example.iq_id}`}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: "100%", flexShrink: 0 }}
    >
      <Box
        sx={{ flex: 1, position: "relative", minHeight: 0 }}
      >
        <ImageCanvas imageData={[image, imageStatus]} annotations={rois || []} iqId={example.iq_id} />
        {showOpenModal && (
          <IconButton
            aria-label="launch"
            className="absolute right-2 top-2 bg-slate-500 bg-opacity-75"
            onClick={handleOpenModal}
          >
            <LaunchIcon className="text-white" />
          </IconButton>
        )}
      </Box>
      <AnswerBar imageQuery={example} answer={example.current_best_prediction_str || ""} />
    </Box>
  );
}

export interface ExampleImageModalProps {
  currentPosicheck: Posicheck;
  exampleImageQuery: ImageQuery;
  isOpen: boolean;
  onClose: () => void;
  examples: ImageQuery[];
  exampleTitle: string;
}

export function ExampleImageModal(props: ExampleImageModalProps) {
  const { currentPosicheck, isOpen, onClose, exampleImageQuery, examples, exampleTitle } = props;

  const userData = useAppSelector(getUserState);
  const [selectedExampleImageQuery, setSelectedExampleImageQuery] = useState<ImageQuery>(exampleImageQuery);
  const [openFocusModal, setOpenFocusModal] = useState(false);
  const dispatch = useAppDispatch();

  const handleOpenModal = useCallback(() => {
    setOpenFocusModal(true);
    dispatch(setIsFocusModalOpen(true));
    dispatch(setUnsavedAnnotations(false));
  }, [dispatch]);

  const handleCloseModal = () => setOpenFocusModal(false);

  const currentIqImage = React.useMemo(() => {
    const img = getIQImage(currentPosicheck.id);
    return img;
  }, [currentPosicheck.id]);

  const exampleLabelColor = getLabelColor(exampleTitle) || "grey-400";

  const modalBody = () => (
    <Grid container spacing={{ xs: 2, md: 4 }} paddingX={1} paddingTop={1} paddingBottom={4} height="100%" data-testid="example-image-modal">
      {/* Left Panel */}
      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack spacing={1}>
          <Typography variant="subtitle">Current Query:</Typography>
          <Divider className="border-y-[1.25px] mb-2 bg-grey-400" />
        </Stack>
        <Box sx={{ flex: 1, width: "100%", position: "relative", minHeight: 0 }}>
          <ImageCanvas imageData={[currentIqImage, "loaded"]} annotations={[]} iqId={currentPosicheck.id} />
        </Box>
      </Grid>
      {/* Right Panel */}
      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack spacing={1} sx={{ flexShrink: 0 }}>
          <Typography variant="subtitle">{`${exampleTitle} Examples` || "No examples available"}:</Typography>
          <Divider className={`border-y-[1.25px] mb-2 bg-${exampleLabelColor ?? "grey-400"}`} />
        </Stack>
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
          {examples.map(ex => (
            <ImageItem
              key={ex.iq_id}
              example={ex}
              showOpenModal={userData.userprofile.customer_type !== UserType.Labeler}
              onOpenModal={handleOpenModal}
              setSelectedExampleImageQuery={setSelectedExampleImageQuery}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Modal
        size="fullScreen"
        open={isOpen}
        onClose={onClose}
        title={currentPosicheck?.predictor.label_prompt}
        body={modalBody()}
      />

      {openFocusModal && (
        <QueryDetailsModal
          loading={false}
          getData={() => Promise.resolve()}
          rows={[selectedExampleImageQuery]}
          rowCount={1}
          selectedRowIndex={0}
          showDialog={openFocusModal}
          onClose={handleCloseModal}
          page={1}
          pageSize={1}
          detector={currentPosicheck.predictor}
          owner={currentPosicheck.owner}
        />
      )}
    </>
  );
}
